import { lazy, Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { I18nextProvider } from 'react-i18next';
import { MemoryRouter, Routes, Route } from 'react-router-dom';
import { X } from 'lucide-react';
import { store } from '@/store';
import i18n from '@/lib/i18n';
import { TooltipProvider } from '@/components/ui/tooltip';
import { TutorialModeProvider } from '@/contexts/TutorialModeContext';
import { useI18nText } from '@/lib/utils/i18nText';
import { useCopyLink } from '@/lib/utils/useCopyLink';
import { PageLoader } from '@/components/Spinner';
import { ChannelDrawerHeaderTitle } from '@/components/ChannelDrawerHeaderTitle';
import { getUserProfile } from '@/lib/api/panel.api';
import { GgcLinkedText } from './ui/ggc/linked-text';

// Lazy: this module is statically reachable from Header.tsx (via ChannelDrawer,
// rendered on every page) — a static import of the full UsersProfiles page here
// would pull it (and everything it imports) into the main entry bundle.
const UsersProfiles = lazy(() => import('@/pages/influencers/profile'));

function InfluencerChatMemoryApp({ userId, campaignId }: { userId: string; campaignId?: string }) {
  const initialPath = campaignId
    ? `/influencer/${userId}/campaigns/${campaignId}`
    : `/influencer/${userId}/campaigns`;
  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18n}>
        <TutorialModeProvider>
        <TooltipProvider>
          <MemoryRouter
            key={userId}
            initialEntries={[initialPath]}
            initialIndex={0}
            future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
          >
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/influencer/:userId" element={<UsersProfiles />} />
              <Route path="/influencer/:userId/campaigns" element={<UsersProfiles />} />
              <Route path="/influencer/:userId/campaigns/:campaignId" element={<UsersProfiles />} />
              <Route path="/influencer/:userId/campaigns/:campaignId/:action" element={<UsersProfiles />} />
              <Route path="/influencer/:userId/stats" element={<UsersProfiles />} />
              <Route path="/influencer/:userId/stats/:action" element={<UsersProfiles />} />
            </Routes>
            </Suspense>
          </MemoryRouter>
        </TooltipProvider>
        </TutorialModeProvider>
      </I18nextProvider>
    </Provider>
  );
}

function InfluencerChatDrawerProfileHost({ userId, campaignId }: { userId: string; campaignId?: string }) {
  const shellRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const inner = document.createElement('div');
    inner.className = 'h-full min-h-0 w-full';
    shell.appendChild(inner);
    const root = createRoot(inner);
    root.render(<InfluencerChatMemoryApp userId={userId} campaignId={campaignId} />);

    return () => {
      // Defer unmount out of the parent's commit phase: unmounting a nested root
      // synchronously while React is rendering triggers a race-condition warning.
      setTimeout(() => {
        try {
          root.unmount();
        } catch {
          /* node already detached, etc. */
        }
        inner.remove();
      });
    };
  }, [userId, campaignId]);

  return <div ref={shellRef} className="h-full min-h-0 w-full" />;
}

/** 대시보드/후보자 페이지 우측 드로어: `/influencer/:id/campaigns`와 동일한 UsersProfiles UI */
export function InfluencerChatDrawerBody({ userId, campaignId, onClose }: { userId: string; campaignId?: string; onClose: () => void }) {
  const { t } = useI18nText();
  const copyLink = useCopyLink();
  const initialPath = campaignId
    ? `/influencer/${userId}/campaigns/${campaignId}`
    : `/influencer/${userId}/campaigns`;
  const [channelInfo, setChannelInfo] = useState<{ title?: string; type?: string; thumbnail?: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setChannelInfo(null);
    getUserProfile(userId)
      .then((data) => {
        if (cancelled) return;
        // Same name-fallback chain as this same channel's Channel-detail pane
        // (panel/user.tsx's UserHeader + ChannelDrawerHeaderTitle: `channels[0].title
        // || profile.name`) — this drawer's two tabs (Details/Chat) show the exact
        // same channel and must never disagree on its name. `channels[0].title` is
        // the live per-channel name (kept fresh by the platform re-scrape); the
        // user-level `title` field is an earlier snapshot that can go stale after a
        // rename without the top-level record being touched again — a prior version
        // of this fallback chain prioritized `title` first for the opposite reason
        // (a case where `channels[0].title` itself was the stale one), but flipping
        // the whole app to one fixed order can't fix both directions of staleness at
        // once — matching the Channel-detail pane's own choice at least keeps this
        // one drawer internally consistent instead of showing two different names
        // for the same channel depending which tab is open.
        const u = data as {
          title?: string;
          profile?: { name?: string; fullname?: string };
          channels?: Array<{ title?: string; type?: string; thumbnail?: string }>;
        };
        const ch = u?.channels?.[0];
        setChannelInfo({
          title: ch?.title || u?.profile?.fullname || u?.profile?.name || u?.title,
          type: ch?.type,
          thumbnail: ch?.thumbnail,
        });
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="panel-user-drawer-toolbar">
        {/* Same `{platform icon} {channel name} | {section}` header as the
            Channel-detail pane, so the two tabs of the merged drawer read
            identically (replaces the old one-off channel badge chip). */}
        <ChannelDrawerHeaderTitle
          section="chat"
          channelType={channelInfo?.type}
          channelName={channelInfo?.title}
          channelThumbnail={channelInfo?.thumbnail}
        />
        <div className="flex shrink-0 items-center gap-3">
          {/* Same actions as the Channel-detail pane's toolbar (copy + open),
              so the two tabs' headers stay symmetric. */}
          <button
            type="button"
            className="ggc-linked-text panel-user-drawer-toolbar-action"
            aria-label={t('Copy this page URL', '이 페이지 URL 복사')}
            onClick={() => copyLink(`${window.location.origin}${initialPath}`)}
          >
            <span className="panel-user-drawer-toolbar-label">{t('Copy this page URL', '이 페이지 URL 복사')}</span>
            <i className='fa fa-copy ml-1' />
          </button>
          <GgcLinkedText
            href={initialPath}
            target="_blank"
            rel="noopener noreferrer"
            className='panel-user-drawer-toolbar-action'
            aria-label={t('Open in new tab', '새 탭에서 열기')}
          >
            {/* Same mobile icon-only collapse as the Channel-detail drawer's toolbar
                (.panel-user-drawer-toolbar-label) — same narrow-header problem. */}
            <span className="panel-user-drawer-toolbar-label">{t('Open in new tab', '새 탭에서 열기')}</span>
            <i className='fa fa-external-link-alt ml-1' />
          </GgcLinkedText>
          <button
            type="button"
            className="rounded-ggc-sm p-1 text-ggc-text-gray hover:bg-gray-200 hover:text-gray-900"
            aria-label={t('Close', '닫기')}
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
      <div className="dashboard-influencer-drawer-scroll min-h-0 flex-1 bg-white">
        <InfluencerChatDrawerProfileHost userId={userId} campaignId={campaignId} />
      </div>
    </div>
  );
}
